/**
 * Diễn giải nội dung tin nhắn Zalo sang dạng người đọc được.
 *
 * Zalo trả rất nhiều loại tin dưới dạng JSON và backend lưu nguyên chuỗi đó vào
 * `content` (cố ý — để không mất dữ liệu gốc). Việc dịch sang chữ đẹp là của
 * frontend. Nguyên tắc bắt buộc:
 *
 *   KHÔNG BAO GIỜ để lọt chuỗi JSON thô ra màn hình.
 *
 * Loại nào chưa nhận diện được thì rơi về nhãn theo `contentType`, chứ không
 * hiển thị `{"eventType":"leave",...}` như trước.
 *
 * Dùng chung cho bong bóng chat (`message-bubble.tsx`) và dòng xem trước ở danh
 * sách hội thoại (`lib.ts`) để hai nơi không bao giờ lệch nhau.
 */

import { i18n } from '@/lib/i18n'

export type MessageView =
  /** Thông báo hệ thống — vẽ thành chip xám ở giữa, không phải bong bóng. */
  | { kind: 'system'; text: string }
  | { kind: 'video'; href: string; thumb?: string; durationMs?: number }
  | { kind: 'card'; title: string; description?: string; thumb?: string; href?: string }
  /** Tin Zalo có link: `text` là TOÀN BỘ chữ khách gõ; `preview` là thẻ xem trước trang (có thể không có). */
  | { kind: 'link'; text: string; href?: string; preview?: LinkPreview }
  | { kind: 'text'; text: string }
  /**
   * Sự kiện bình chọn nhóm KÈM `pollId` dùng được (gọi API lấy chi tiết) — `text` là dòng thông báo
   * (chip xám) như trước, `pollId` để `PollCardBubble` tải câu hỏi/lựa chọn/số phiếu thật từ Zalo.
   */
  | { kind: 'poll'; text: string; pollId: number }

export interface LinkPreview {
  title?: string
  description?: string
  thumb?: string
  /** Tên miền / nguồn Zalo ghi sẵn, vd "tiktok.com". */
  site?: string
}

interface Msg {
  content: string | null
  contentType: string
  senderName?: string | null
}

/** Nhãn cuối cùng khi không rút được nội dung nào có nghĩa. */
const TYPE_LABEL: Record<string, string> = {
  get image() { return i18n.t('convChat:messageLabel.image') },
  get video() { return i18n.t('convChat:messageLabel.video') },
  get file() { return i18n.t('convChat:messageLabel.file') },
  get sticker() { return i18n.t('convChat:messageLabel.sticker') },
  get gif() { return i18n.t('convChat:messageLabel.gif') },
  get voice() { return i18n.t('convChat:messageLabel.voice') },
  get audio() { return i18n.t('convChat:messageLabel.audio') },
  get location() { return i18n.t('convChat:messageLabel.location') },
  get card() { return i18n.t('convChat:messageLabel.card') },
  get contact_card() { return i18n.t('convChat:messageLabel.contact_card') },
  get bank_card() { return i18n.t('convChat:messageLabel.bank_card') },
  get link() { return i18n.t('convChat:messageLabel.link') },
  get call() { return i18n.t('convChat:messageLabel.call') },
  get group_event() { return i18n.t('convChat:messageLabel.group_event') },
  get birthday_notification() { return i18n.t('convChat:messageLabel.birthday_notification') },
  get rich() { return i18n.t('convChat:messageLabel.rich') },
  get doodle() { return i18n.t('convChat:messageLabel.doodle') },
  get poll() { return i18n.t('convChat:messageLabel.poll') },
}

/** Sự kiện gắn với một người cụ thể → phải ghép tên vào trước nhãn. */
const MEMBER_EVENTS = new Set([
  'join', 'leave', 'remove_member', 'block_member', 'add_admin', 'remove_admin',
])

function parseJson(raw: unknown): Record<string, unknown> | null {
  if (typeof raw !== 'string') return null
  const t = raw.trim()
  if (!t.startsWith('{') || !t.endsWith('}')) return null
  try {
    const o = JSON.parse(t) as unknown
    return o && typeof o === 'object' && !Array.isArray(o) ? (o as Record<string, unknown>) : null
  } catch {
    return null
  }
}

const str = (v: unknown): string => (typeof v === 'string' ? v.trim() : '')

/** Chuỗi trông như JSON (object hoặc mảng) mà không rút được nội dung nào. */
function looksLikeJson(text: string): boolean {
  if (!/^[{[]/.test(text)) return false
  try {
    JSON.parse(text)
    return true
  } catch {
    return false
  }
}

/** Zalo nhét thêm một chuỗi JSON nữa vào trường `params`. */
function params(obj: Record<string, unknown>): Record<string, unknown> {
  return parseJson(obj.params) ?? {}
}

function formatDuration(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return ''
  const m = Math.floor(seconds / 60)
  const s = Math.round(seconds % 60)
  if (!m) return i18n.t('convChat:messageContent.durationSeconds', { s })
  return s ? i18n.t('convChat:messageContent.durationMinSec', { m, s }) : i18n.t('convChat:messageContent.durationMinutes', { m })
}

/** Sự kiện nhóm: vào/ra nhóm, đổi tên, đổi ảnh… */
function groupEvent(obj: Record<string, unknown>, senderName?: string | null): string {
  const type = str(obj.eventType)
  const label = str(obj.label) || str(obj.displayText)
  const who = str(obj.memberNames) || str(senderName)

  if (MEMBER_EVENTS.has(type)) {
    // Nhãn của các sự kiện này là vế sau ("đã rời nhóm") — thiếu tên thì câu cụt.
    // "Thành viên" = người trong nhóm chat (thường là khách / người ngoài tổ chức); "nhân viên" chỉ dành cho người của tổ chức.
    return who ? `${who} ${label}` : i18n.t('convChat:messageContent.aMember', { label })
  }
  return label || str(obj.displayText) || i18n.t('convChat:messageContent.groupEvent')
}

/** Bong bóng cuộc gọi (`sendBubbleMessage` + action `recommened.*`). */
function callEvent(obj: Record<string, unknown>): string {
  const p = params(obj)
  const action = str(obj.action)
  const video = p.calltype === 1
  const kind = video ? i18n.t('convChat:messageContent.videoCall') : i18n.t('convChat:messageContent.voiceCall')
  if (action.includes('misscall')) return i18n.t('convChat:messageContent.missedCall', { kind })
  const d = formatDuration(Number(p.duration))
  return d ? i18n.t('convChat:messageContent.callWithDuration', { kind, duration: d }) : kind
}

/**
 * Sự kiện bình chọn nhóm (`msgType: 'group.poll'`, action `create`/`vote`…). Zalo không gửi thẳng câu
 * hỏi/lựa chọn trong tin nhắn — đây là dòng THÔNG BÁO ai vừa tạo/tham gia bình chọn nào (câu hỏi có sẵn
 * trong `params.question`, không phải "nội dung bình chọn" đầy đủ với các lựa chọn — Zalo gốc cũng chỉ
 * hiện đúng dòng thông báo này trong khung chat, phần chọn đáp án mở ở màn riêng). `params.msg.vi` là
 * CÂU MẪU Zalo tự soạn sẵn (`"%1$s tạo cuộc bình chọn mới: %2$s"`) — dùng thẳng để khớp đúng chữ Zalo
 * gốc cho mọi loại hành động (create/vote/…), không tự đặt câu riêng kẻo thiếu khi Zalo thêm hành động mới.
 */
function pollEvent(obj: Record<string, unknown>): string {
  const p = params(obj)
  const question = str(p.question)
  const dName = str(p.dName) || i18n.t('convChat:messageContent.aMemberShort')
  const msgTemplates = p.msg && typeof p.msg === 'object' ? (p.msg as Record<string, unknown>) : null
  const template = msgTemplates ? str(msgTemplates.vi) : ''
  if (template && question) return template.replace('%1$s', dName).replace('%2$s', question)
  if (question) {
    const action = str(obj.action)
    return action === 'vote'
      ? i18n.t('convChat:messageContent.pollVoted', { name: dName, question })
      : i18n.t('convChat:messageContent.pollCreated', { name: dName, question })
  }
  return TYPE_LABEL.poll
}

/** Dòng đã có sẵn ký hiệu đầu dòng do khách tự gõ → không thêm lần nữa. */
const HAS_MARKER = /^\s*([•●▪◦\-*+]|\d+[.)])\s/

/**
 * Tin chữ có định dạng của Zalo (`action: 'rtf'`) → chữ thường: giữ xuống dòng, thêm "• " cho dòng gạch đầu dòng
 * (`lst_1`) và "1. ", "2. "… cho danh sách số (`lst_2`) — Zalo chỉ đánh dấu bằng `params.styles`, không có trong chữ.
 * Khớp `zaloRtfText` ở backend (`shared/zalo-text-content.ts`).
 */
function rtfText(obj: Record<string, unknown>): string {
  const title = typeof obj.title === 'string' ? obj.title : ''
  const raw = params(obj).styles
  const styles = Array.isArray(raw) ? (raw as Record<string, unknown>[]) : []
  const lists = styles.flatMap((s) => {
    const start = Number(s?.start)
    const len = Number(s?.len)
    const st = String(s?.st ?? '').split(',').map((x) => x.trim())
    const kind = st.includes('lst_2') ? 'number' : st.includes('lst_1') ? 'bullet' : null
    return kind && Number.isFinite(start) && Number.isFinite(len) ? [{ start, len, kind }] : []
  })

  let offset = 0
  let counter = 0
  return title
    .split('\n')
    .map((line) => {
      const start = offset
      const end = start + line.length
      offset = end + 1
      // Khoảng định dạng bắt đầu trong dòng này (dòng trống: đúng tại đầu dòng), hoặc trải từ dòng trước sang.
      const hit = lists.find((s) =>
        (s.start >= start && (s.start < end || s.start === start)) || (s.start < start && s.start + s.len > start))
      counter = hit?.kind === 'number' ? counter + 1 : 0
      if (!hit || !line.trim() || HAS_MARKER.test(line)) return line
      return hit.kind === 'number' ? `${counter}. ${line}` : `• ${line}`
    })
    .join('\n')
    .trim()
}

/**
 * Các loại đã có bộ vẽ riêng trong `message-bubble.tsx` (ảnh, tệp, sticker,
 * danh thiếp, thẻ ngân hàng). Ở đây chỉ trả nhãn cho dòng xem trước, không
 * được trả `card`/`video` kẻo giành mất chỗ vẽ của chúng.
 */
const HAS_OWN_RENDERER = new Set(['image', 'file', 'sticker', 'card', 'bank_card'])

/**
 * Chuyển một tin nhắn thành dạng hiển thị.
 * Luôn trả về thứ đọc được — không bao giờ trả JSON thô.
 */
export function describeMessage(msg: Msg): MessageView {
  const type = msg.contentType
  const raw = msg.content ?? ''
  const obj = parseJson(raw)

  // Sticker lưu đủ kiểu: JSON, id số trần, hoặc URL — không dạng nào đọc được,
  // luôn trả nhãn. Các loại còn lại chỉ thay nhãn khi nội dung là JSON, để giữ
  // phần chữ đẹp sẵn có (ví dụ ảnh lưu "[📷 ten-tep.png]").
  if (type === 'sticker') return { kind: 'text', text: TYPE_LABEL.sticker }
  if (obj && HAS_OWN_RENDERER.has(type)) {
    return { kind: 'text', text: TYPE_LABEL[type] ?? i18n.t('convChat:messageLabel.message') }
  }

  // Không phải object JSON → hoặc là chữ thường, hoặc là JSON dạng khác (Zalo
  // gửi biên nhận đã-nhận/đã-xem dưới dạng MẢNG). Mảng thì không có gì để vẽ,
  // phải ra nhãn — nếu trả nguyên chuỗi là lòi mã ra màn hình.
  if (!obj) {
    const text = raw.trim()
    if (!text || looksLikeJson(text)) return { kind: 'text', text: TYPE_LABEL[type] ?? i18n.t('convChat:messageLabel.message') }
    return { kind: 'text', text }
  }

  // Tin chữ có định dạng (đậm, màu, gạch đầu dòng…): Zalo gửi `webchat` + action `rtf`, `title` là TOÀN BỘ chữ.
  // Từ 09/10/2026 backend lưu thành chữ thường; tin cũ còn lưu nguyên JSON (loại `text`) → chuyển ở đây.
  if (str(obj.action) === 'rtf') {
    const text = rtfText(obj)
    if (text) return { kind: 'text', text }
  }

  // Tin chữ có chứa URL: Zalo gửi `chat.recommended` + action `recommened.link`, `title` là TOÀN BỘ chữ khách gõ
  // (brief, báo giá… có khi cả nghìn ký tự), `params.mediaTitle` mới là tiêu đề trang. Bản cũ lưu nhầm loại
  // `contact_card` → nhận theo hình dạng nội dung, không theo `contentType`.
  if (str(obj.action) === 'recommened.link') {
    const href = str(obj.href)
    const text = str(obj.title) || href
    if (text) {
      const p = params(obj)
      const preview: LinkPreview = {
        title: str(p.mediaTitle) || undefined,
        description: str(obj.description) || undefined,
        thumb: str(obj.thumb) || undefined,
        site: str(p.src) || undefined,
      }
      const hasPreview = !!(preview.title || preview.description || preview.thumb)
      return { kind: 'link', text, href: href || undefined, preview: hasPreview ? preview : undefined }
    }
  }

  switch (type) {
    case 'group_event':
      return { kind: 'system', text: groupEvent(obj, msg.senderName) }
    case 'call':
      return { kind: 'system', text: callEvent(obj) }
    case 'poll': {
      const text = pollEvent(obj)
      const pollId = Number(params(obj).pollId)
      // Có pollId hợp lệ mới vẽ được thẻ thật (gọi API lấy chi tiết) — thiếu thì giữ chip chữ như trước.
      return Number.isFinite(pollId) && pollId > 0 ? { kind: 'poll', text, pollId } : { kind: 'system', text }
    }
    case 'birthday_notification': {
      // `notifyTxt` là câu hoàn chỉnh Zalo dựng sẵn, đẹp hơn title rời rạc.
      const p = params(obj)
      const text = str(p.notifyTxt) || [str(obj.title), str(obj.description)].filter(Boolean).join(' — ')
      return { kind: 'system', text: text || i18n.t('convChat:messageContent.birthdayReminder') }
    }
    case 'video':
      if (str(obj.href)) {
        return {
          kind: 'video',
          href: str(obj.href),
          thumb: str(obj.thumb) || undefined,
          durationMs: Number(params(obj).duration) || undefined,
        }
      }
      break
  }

  // Gói tin thu hồi lọt vào bảng tin (backend phân loại là `rich`): không có nội
  // dung để vẽ, chỉ có id tin bị gỡ.
  if (obj.globalMsgId !== undefined && 'deleteMsg' in obj) {
    return { kind: 'system', text: i18n.t('convChat:messageContent.messageRecalled') }
  }

  // Dạng thẻ chung của Zalo: title + description + thumb (+ href).
  const title = str(obj.title)
  const description = str(obj.description)
  if (title || description) {
    // Danh thiếp nhét JSON vào `description` — rút số điện thoại ra cho dễ đọc.
    const inner = parseJson(description)
    const phone = inner ? str(inner.phone) : ''
    return {
      kind: 'card',
      title: title || i18n.t('convChat:messageContent.sharedContent'),
      description: phone || (inner ? '' : description) || undefined,
      thumb: str(obj.thumb) || undefined,
      href: str(obj.href) || undefined,
    }
  }

  // Hết cách rút chữ → nhãn theo loại. Tuyệt đối không trả JSON.
  return { kind: 'text', text: TYPE_LABEL[type] ?? i18n.t('convChat:messageLabel.message') }
}

/** Một dòng gọn cho danh sách hội thoại. */
export function previewText(msg: Msg): string {
  const v = describeMessage(msg)
  switch (v.kind) {
    case 'system':
      return v.text
    case 'video':
      return TYPE_LABEL.video
    case 'card':
      return v.title
    case 'link':
    case 'text':
    case 'poll':
      return v.text
  }
}
